import { Component, Input } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { SupportChannel } from 'app/main/services/companyConfig.service';

@Component({
  selector: 'app-navbar-custom-support',
  templateUrl: './navbar-custom-support.component.html',
  styleUrls: ['./navbar-custom-support.component.scss'],
})
export class NavbarCustomSupportComponent {
  @Input() customSupportContent: string = '';
  @Input() customSupportChannels: SupportChannel[] = [];
  @Input() isCustomSupportContent: boolean = false;
  @Input() companyLogo: string = '';

  constructor(
    private activeModal: NgbActiveModal,
    private sanitizer: DomSanitizer
  ) {}

  closeModal(): void {
    console.log('Closing modal...');
    this.activeModal.close();
  }

  get sanitizedContent(): SafeHtml {
    const processedContent = this.processShortcodes(
      this.customSupportContent || ''
    );
    // Usar bypassSecurityTrustHtml para permitir HTML y enlaces
    return this.sanitizer.bypassSecurityTrustHtml(processedContent);
  }

  get hasActiveChannels(): boolean {
    return (
      this.customSupportChannels &&
      this.customSupportChannels.some((ch) => ch.isActive)
    );
  }

  shouldShowSupportCard(): boolean {
    if (!this.hasActiveChannels) {
      return false;
    }
    if (!this.customSupportContent) {
      return true;
    }
    return !this.customSupportContent.includes('support-card');
  }

  openChannel(channel: SupportChannel): void {
    const redirectUrl = this.getChannelRedirectUrl(channel);
    if (redirectUrl) {
      window.open(redirectUrl, '_blank');
    }
  }

  private getChannelRedirectUrl(channel: SupportChannel): string {
    switch (channel.type) {
      case 'email':
        return `mailto:${channel.value}`;
      case 'phone':
        return `tel:${channel.value}`;
      case 'whatsapp':
        return `https://wa.me/${channel.value.replace(/[^0-9]/g, '')}`;
      case 'website':
        return channel.value;
      default:
        return '#';
    }
  }

  private processShortcodes(content: string): string {
    if (!content) return '';
    let result = content;

    // [[support_channels]] => reemplazar con el HTML completo de la tarjeta
    result = result.replace(/\[\[support_channels\]\]/g, () => {
      return this.buildChannelsCardHTML();
    });

    // [[support_channel:N]] => generar HTML del canal específico
    result = result.replace(/\[\[support_channel:(\d+)\]\]/g, (_m, g1) => {
      const idx = parseInt(g1, 10) - 1;
      const channel = this.customSupportChannels.filter((c) => c.isActive)[idx];
      return channel ? this.buildChannelRowHTML(channel) : '';
    });

    return result;
  }

  private buildChannelsCardHTML(): string {
    const activeChannels = this.customSupportChannels.filter(
      (ch) => ch.isActive
    );
    if (!activeChannels.length) return '';

    const channelIcons = activeChannels
      .map((ch) => {
        const iconClass = this.getChannelIcon(ch.type);
        const tooltip = this.getChannelTooltip(ch);
        const redirectUrl = this.getChannelRedirectUrl(ch);
        return `<a href="${redirectUrl}" 
                   target="_blank" 
                   rel="noopener noreferrer" 
                   class="channel-icon-container text-decoration-none" 
                   title="${tooltip}" 
                   aria-label="Abrir ${ch.type}: ${ch.value}"
                   style="cursor: pointer;">
                <div class="channel-icon channel-${ch.type}">
                  <i class="${iconClass}"></i>
                </div>
              </a>`;
      })
      .join('');

    const email = this.getRequiredEmail();
    const phone = this.getRequiredPhone();
    const contactInfo =
      email || phone
        ? `
      <div class="contact-info">
        <div class="row g-2">
          ${
            email
              ? `
            <div class="col-6">
              <div class="info-item">
                <label class="info-label">Email</label>
                <div class="info-value" title="${email}">${email}</div>
              </div>
            </div>
          `
              : ''
          }
          ${
            phone
              ? `
            <div class="col-6">
              <div class="info-item">
                <label class="info-label">Teléfono</label>
                <div class="info-value" title="${phone}">${phone}</div>
              </div>
            </div>
          `
              : ''
          }
        </div>
      </div>
    `
        : '';

    return `<div class="card support-card border-0 shadow-sm">
              <div class="card-body text-center p-4">
                <div class="company-logo mb-3">
                  <div class="logo-container">
                    ${
                      this.companyLogo
                        ? `<img src="${this.companyLogo}" alt="Company Logo" class="company-logo-img" style="max-width: 120px; height: auto; object-fit: contain;" />`
                        : `<div class="logo-placeholder">
                        <i class="fa-solid fa-building text-primary"></i>
                      </div>`
                    }
                  </div>
                </div>
                <div class="company-info mb-4">
                  <h5 class="company-name mb-1">Soporte al Cliente</h5>
                  <p class="company-description text-muted mb-3">
                    Estamos aquí para ayudarte con cualquier consulta
                  </p>
                  ${contactInfo}
                </div>
                <div class="contact-channels">
                  ${channelIcons}
                </div>
              </div>
            </div>`;
  }

  private getRequiredEmail(): string {
    const emailChannel = this.customSupportChannels.find(
      (ch) => ch.type === 'email' && ch.isActive
    );
    return emailChannel ? emailChannel.value : '';
  }

  private getRequiredPhone(): string {
    const phoneChannel = this.customSupportChannels.find(
      (ch) => ch.type === 'phone' && ch.isActive
    );
    return phoneChannel ? phoneChannel.value : '';
  }

  private getChannelTooltip(channel: SupportChannel): string {
    const label = channel.label || this.getChannelTypeLabel(channel.type);
    return `${label}: ${channel.value}`;
  }

  private getChannelTypeLabel(type: string): string {
    switch (type) {
      case 'email':
        return 'Email';
      case 'phone':
        return 'Teléfono';
      case 'whatsapp':
        return 'WhatsApp';
      case 'website':
        return 'Sitio Web';
      default:
        return 'Canal';
    }
  }

  private buildChannelRowHTML(channel: SupportChannel): string {
    const iconClass = this.getChannelIcon(channel.type);
    const value = (channel.value || channel.label || '').replace(
      /"/g,
      '&quot;'
    );
    const redirectUrl = this.getChannelRedirectUrl(channel);

    return `<a href="${redirectUrl}" 
               target="_blank" 
               rel="noopener noreferrer" 
               class="d-flex align-items-center py-1 text-decoration-none text-dark support-channel-link" 
               style="cursor: pointer;">
              <i class="${iconClass} me-2" style="width:18px;"></i>
              <span style="word-break:break-all;">${value}</span>
            </a>`;
  }

  private getChannelIcon(type: string): string {
    switch (type) {
      case 'email':
        return 'fa-solid fa-envelope';
      case 'phone':
        return 'fa-solid fa-phone';
      case 'whatsapp':
        return 'fa-brands fa-whatsapp';
      case 'website':
        return 'fa-solid fa-globe';
      default:
        return 'fa-solid fa-circle-question';
    }
  }
}
